import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { styled } from "@mui/material/styles";
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    Toolbar,
    Typography,
    Box,
    Divider,
    Collapse
} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import BarChartIcon from "@mui/icons-material/BarChart";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import logo from "./logo.svg"; // ✅ Import your logo.svg

const drawerWidth = 250;
const collapsedWidth = 80;

const StyledNavLink = styled(NavLink)(({ theme }) => ({
    textDecoration: "none",
    color: "inherit",
    "&.active": {
        backgroundColor: "#EDE7F6", 
        borderRadius: "5px",
    },
    "&:hover": {
        backgroundColor: "#f5f5f5", 
        textDecoration: "none",
        color: "#7f5fb0"
    }
}));

const Sidebar = ({ open, toggleDrawer }) => {
    const [blocksOpen, setBlocksOpen] = useState(false);
    const [workflowOpen, setWorkflowOpen] = useState(false);

    return (
        <Drawer
            variant="permanent"
            sx={{
                width: open ? drawerWidth : collapsedWidth,
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                    width: open ? drawerWidth : collapsedWidth,
                    transition: "width 0.3s ease-in-out",
                    overflowX: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    height: "100vh",
                    borderRight: "solid 1px lightgrey"
                },
            }}
        >
            {/* Toolbar Header with Logo */}
            <Toolbar
                sx={{
                    display: "flex",
                    justifyContent: open ? "flex-start" : "center",
                    alignItems: "center",
                    padding: "8px",
                    gap: 1 
                }}
            >
                <img src={logo} alt="Logo" width={20} height={20} /> 
                {open ? (
                    <Typography variant="h6">QO Math Unified</Typography>
                ) : (
                    <Typography variant="h6">MU</Typography>
                )}
            </Toolbar>

            {/* Main Items Section */}
            <Box sx={{ flexGrow: 1 }}>
                <Typography
                    variant="body2"
                    sx={{
                        padding: "10px 16px",
                        color: "gray",
                        display: open ? "block" : "none",
                    }}
                >
                    Main items
                </Typography>

                <List>
                    <ListItem button onClick={() => setBlocksOpen(!blocksOpen)}>
                        <ListItemIcon>
                            <GridViewIcon />
                        </ListItemIcon>
                        {open && <ListItemText primary="Blocks" />}
                        {open && (blocksOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                    </ListItem>

                    <Collapse in={blocksOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ pl: open ? 4 : 2 }}>
                            <ListItem component={StyledNavLink} to="/lvcc">
                                <ListItemIcon>
                                    <DescriptionOutlinedIcon />
                                </ListItemIcon>
                                {open && <ListItemText primary="LVCC V1" />}
                            </ListItem>
                            <ListItem component={StyledNavLink} to="/vitros">
                                <ListItemIcon>
                                    <DescriptionOutlinedIcon />
                                </ListItemIcon>
                                {open && <ListItemText primary="Vitros V6" />}
                            </ListItem>
                        </List>
                    </Collapse>

                    <ListItem button onClick={() => setWorkflowOpen(!workflowOpen)}>
                        <ListItemIcon>
                            <GridViewIcon />
                        </ListItemIcon>
                        {open && <ListItemText primary="Workflow" />}
                        {open && (workflowOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                    </ListItem>
                    <Collapse in={workflowOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ pl: open ? 4 : 2 }}>
                            <ListItem component={StyledNavLink} to="/g06">
                                <ListItemIcon>
                                    <DescriptionOutlinedIcon />
                                </ListItemIcon>
                                {open && <ListItemText primary="G06" />}
                            </ListItem>
                            <ListItem component={StyledNavLink} to="/MicroTip">
                                <ListItemIcon>
                                    <DescriptionOutlinedIcon />
                                </ListItemIcon>
                                {open && <ListItemText primary="MicroTip" />}
                            </ListItem>
                            <ListItem component={StyledNavLink} to="/microslide">
                                <ListItemIcon>
                                    <DescriptionOutlinedIcon />
                                </ListItemIcon>
                                {open && <ListItemText primary="MicroSlide" />}
                            </ListItem>
                        </List>
                    </Collapse>

                    <ListItem component={StyledNavLink} to="/api-docs">
                        <ListItemIcon>
                            <ContentPasteIcon />
                        </ListItemIcon>
                        {open && <ListItemText primary="API Docs" />}
                    </ListItem>
                </List>

                <Divider />

                {/* Analytics Section */}
                <Typography
                    variant="body2"
                    sx={{
                        padding: "10px 16px",
                        color: "gray",
                        display: open ? "block" : "none",
                    }}
                >
                    Analytics
                </Typography>
                <List>
                    <ListItem component={StyledNavLink} to="/reports">
                        <ListItemIcon>
                            <BarChartIcon />
                        </ListItemIcon>
                        {open && <ListItemText primary="Reports" />}
                    </ListItem>

                    <ListItem component={StyledNavLink} to="/experimental">
                        <ListItemIcon>
                            <ScienceOutlinedIcon />
                        </ListItemIcon>
                        {open && <ListItemText primary="Experimental" />}
                    </ListItem>
                </List>
            </Box>

            {/* Drawer Toggle Button */}
            <Box sx={{ display: "flex", justifyContent: "center", p: 2, mt: "auto", borderRight: "solid 1px lightgrey" }}>
                <IconButton onClick={toggleDrawer}>
                    {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>
            </Box>
        </Drawer>
    );
};

export default Sidebar;